import React, { useMemo } from 'react';
import { RichText } from '@sitecore-jss/sitecore-jss-react';
import { EVENT_TITLE_DICTIONARY_KEY } from '../../constants/eventsFairs.constants';
import { shouldPioritizeComponentResources } from '../../utils/component.utils';
import { CustomText } from '../@shared/customText';
import DictionaryTerm from '../@shared/dictionaryTerm';
import Picture from '../@shared/picture';
import SocialMediaBar from '../@shared/socialMediaBar';
import UnreleasedComponent from '../@shared/unreleasedComponent';
import SaveCalendarEvent from '../@shared/saveCalendarEvent';
import { TSaveCalendarEventButtonType } from '../@shared/saveCalendarEvent/types/saveEvent.types';
import { FairHeroModel } from './models/fairHero.model';
import { TFairHeroProps } from './types/fairHero.types';
import FadeDownEffect from '../@shared/fadeDownEffect';
import useSitecoreContext from '../../hooks/useSitecoreContext';

const FairHero = (props: TFairHeroProps): JSX.Element => {
  const model = useMemo(() => new FairHeroModel(props), [props]);
  const {sitecoreContext} = useSitecoreContext();
  const placeholderData = sitecoreContext?.route?.placeholders['jss-main'] || [];
  const shouldPioritize = useMemo(() => shouldPioritizeComponentResources(placeholderData, 'FairHero'), [placeholderData]);

  // Component is unreleased
  if (model.isUnreleased) {
    return <UnreleasedComponent />;
  }

  return (
    <section className={'fair-hero'}>
      {model.socialMediaBarProps && <SocialMediaBar {...model.socialMediaBarProps} extraClassName="fair-hero__social-media-bar" />}
      <div className="fair-hero__media">
        <Picture
          editorialImageProps={{
            id: props.id,
            desktopImage: model.image,
            tabletImage: model.mobileImage,
            mobileImage: model.mobileImage,
            desktopTransformation: 'Size1900',
            tabletTransformation: 'Size1000',
            mobileTransformation: 'Size1100',
            alt: model?.image?.value?.alt as string,
            cssClassImageEl: 'fair-hero__image',
            prioritize: shouldPioritize,
          }}
        />
      </div>
      <div className="fair-hero__section">
        <div className="fair-hero__content-wrapper">
          <div className="fair-hero__content">
            <FadeDownEffect delay={1000}>
              <p className="fair-hero__eyebrow">
                <DictionaryTerm keyword={EVENT_TITLE_DICTIONARY_KEY.FAIR} />
              </p>
              </FadeDownEffect>
            <FadeDownEffect delay={2000}>
              <CustomText tag={'h1'} field={model?.title} className="fair-hero__title" />
              </FadeDownEffect>
            <FadeDownEffect delay={3000}>
              <RichText tag="div" field={model?.shortDescription} className="fair-hero__description rich-text rich-text-anchor" />
              </FadeDownEffect>
              {!!model.calendarDetails?.length && (
                <div className="fair-hero__button-wrapper">
                  <SaveCalendarEvent
                    buttonType={TSaveCalendarEventButtonType['PRIMARYWHITE']}
                    calendarDetails={model.calendarDetails}
                    startDate={model.startDate?.value}
                    endDate={model.endDate?.value}
                  />
                </div>
              )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FairHero;
